import React, { useState } from 'react';
import { ArrowRightIcon } from 'lucide-react';
import { InView } from '../InView';
import { PhoneSnak } from '../PhoneSnak';
import { FloatingHearts } from '../FloatingHearts';
import { FerringWordmark } from '../../FerringWordmark';
import { PresentationLogo } from '../../PresentationLogo';
import { ConceptModal } from '../../concept/ConceptModal';
import { journeySteps } from '../../../data/ferringConcept';
const WORDS = ['Short.', 'Sharp.', 'Satisfying.'];
export function OpeningSection() {
  const [conceptOpen, setConceptOpen] = useState(false);
  return <section id="opening" aria-label="All you need is love" className="relative flex min-h-screen w-full flex-col overflow-hidden bg-[var(--surface)]" data-id="element-493" data-mp="2kkdsi8scer39">
      <FloatingHearts data-id="element-494" data-mp="2frkmlhl51cpl" />

      <div className="relative flex items-center justify-between gap-4 px-8 pt-6 lg:px-14" data-id="element-495" data-mp="1s43123sgvav2">
        <div className="flex items-center gap-4" data-id="element-496" data-mp="1o2hhy0pca0cf">
          <FerringWordmark data-id="element-497" data-mp="1o1pw3hljngzc" />
          <span className="h-6 w-px bg-[var(--fg)]/15" aria-hidden="true" data-id="element-498" data-mp="1o0ev1pjhgnbx" />
          <span className="text-[10px] font-semibold uppercase tracking-[0.24em] text-[var(--fg)]/45" data-id="element-499" data-mp="3q8trlyaah2oa">
            REKOVELLE · Snakker concept
          </span>
        </div>
        <PresentationLogo data-id="element-500" data-mp="3tiieszjjtsl" />
      </div>

      <main className="relative flex flex-1 items-center" data-id="element-501" data-mp="3uk37p6czuni">
        <div className="mx-auto grid w-full max-w-[1440px] items-center gap-14 px-8 py-12 lg:grid-cols-[minmax(0,1fr)_auto] lg:gap-20 lg:px-14 lg:py-8" data-id="element-502" data-mp="8t4uv07jp60c">
          <div className="max-w-[620px]" data-id="element-503" data-mp="37cva0beummvi">
            <InView data-id="element-504" data-mp="3c8sglq0b8ev3">
              <h1 className="text-[58px] font-extrabold leading-[0.92] tracking-[-0.055em] text-[var(--fg)] sm:text-[84px]" data-id="element-505" data-mp="3cadcpk29w5v2">
                All you need is love.
              </h1>
            </InView>
            <InView delay={0.1} data-id="element-506" data-mp="3m9ckl0f7rxh0">
              <p className="mt-7 max-w-[30ch] text-[21px] font-semibold leading-[1.3] tracking-[-0.01em] text-[var(--muted)]" data-id="element-507" data-mp="3m5f0ocktb1w2">
                Attention makes things thrive. Does REKOVELLE get the attention it deserves?
              </p>
              <p className="mt-5 flex flex-wrap items-center gap-x-4 gap-y-1" data-id="element-508" data-mp="2xjvy2be1dx8b">
                {WORDS.map((word, i) => <span key={word} className="flex items-center gap-4" data-id="element-509" data-mp="2sl2k6huu847m" data-mpi={i}>
                    {i > 0 && <span aria-hidden="true" className="h-1.5 w-1.5 rounded-full bg-[var(--brand)]/40" data-id="element-510" data-mp="36kc1xlyoz6dp" />}
                    <span className="text-[22px] font-extrabold tracking-[-0.03em] text-[var(--brand)]" data-id="element-511" data-mp="32l370drx12gk">{word}</span>
                  </span>)}
              </p>
            </InView>

            <InView delay={0.2} className="mt-10" data-id="element-512" data-mp="29w5smlqtrpt3">
              <div className="rounded-2xl bg-white p-5 ring-1 ring-[var(--fg)]/5" data-id="element-513" data-mp="2nmhi0cw0zagj">
                <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[var(--brand)]" data-id="element-514" data-mp="2nolx7qzdjvw4">
                  The idea in one line
                </p>
                <p className="mt-1.5 text-[16px] font-bold leading-snug text-[var(--fg)]" data-id="element-515" data-mp="1v558vkkmo9hn">
                  Start with the clinical idea — an appropriate ovarian response — and let REKOVELLE enter as the answer.
                </p>
                <ol className="mt-3 flex flex-wrap gap-1.5" aria-label="Teaser journey" data-id="element-516" data-mp="1gylcvp9jazm8">
                  {journeySteps.map((step, i) => <li key={step.label} className={`rounded-full px-2.5 py-1 text-[11.5px] font-bold ${step.highlight ? 'bg-[var(--brand)] text-white' : 'bg-[var(--surface)] text-[var(--fg)]'}`} data-id="element-517" data-mp="1730fy5jbopjc" data-mpi={i}>
                      <span className={step.highlight ? 'text-white/70' : 'text-[var(--muted)]'} data-id="element-518" data-mp="24qimd9pihfk6">{i + 1}</span> {step.label}
                    </li>)}
                </ol>
              </div>
            </InView>

            <InView delay={0.28} className="mt-8 flex flex-wrap items-center gap-3" data-id="element-519" data-mp="1uqi2nmnqcxfg">
              <button type="button" onClick={() => setConceptOpen(true)} className="inline-flex items-center gap-2 rounded-full bg-[var(--brand)] px-6 py-3.5 text-[14px] font-bold text-white shadow-sm transition-[background-color,transform] duration-150 hover:bg-[var(--fg)] active:scale-[0.97] focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand)] focus-visible:ring-offset-2" data-id="element-520" data-mp="ed1pq6te6md3">
                Full concept &amp; strategy
                <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-521" data-mp="kwpm3984r07s" />
              </button>
            </InView>
          </div>

          <InView delay={0.16} y={26} className="flex justify-center lg:justify-end" data-id="element-522" data-mp="1igk9w9aewo8o">
            <div className="flex flex-col items-center gap-5" data-id="element-523" data-mp="1dhqvmenawt7g">
              <span className="text-[10px] font-semibold uppercase tracking-[0.24em] text-[var(--fg)]/40" data-id="element-524" data-mp="18jpg23p33c0q">
                Live prototype
              </span>
              <PhoneSnak label="Live REKOVELLE teaser Snak" className="h-[clamp(560px,calc(100vh-240px),800px)]" data-id="element-525" data-mp="3tay981rvuysk" />
              <span className="text-[11px] font-medium tracking-[0.02em] text-[var(--muted)]" data-id="element-526" data-mp="3oaboosy2mljv">
                Tap to play · about 40 seconds
              </span>
            </div>
          </InView>
        </div>
      </main>

      <footer className="relative px-8 pb-6 lg:px-14" data-id="element-527" data-mp="28qkoqszsbt6u">
        <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-[var(--fg)]/30" data-id="element-528" data-mp="2dr8ezcg6w600">
          Concept &amp; creative direction © Snakker™ 2026 · Subject to Ferring medical/legal review
        </p>
      </footer>

      <ConceptModal open={conceptOpen} onClose={() => setConceptOpen(false)} data-id="element-529" data-mp="2nhjsnqpodtk3" />
    </section>;
}